/* eslint-disable no-console */

import React, { useState, useEffect } from 'react';
import CaseStudyStyle from './style';
import { Input, Select, TableProps, message } from 'antd';
import { Button, Image, Pagination } from 'src/components';
import {
  SearchOutlined,
  DeleteOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { SpinStyle } from 'src/components/style';

import DeleteModal from './delete-modal/DeleteModal';
import FormModal from './form-modal/FormModal';

import { useAppDispatch, useAppSelector } from 'src/stores';
import {
  deleteCaseStudiesAction,
  getCaseStudiesAction,
} from 'src/stores/screens/caseStudy/caseStudy.action';
import { getCategoryAction } from 'src/stores/screens/category/category.action';
import { CASE_STUDY_STATUS, CASE_STUDY_STATUS_OPTIONS, LANGUAGE } from 'src/constants';
import { capitalizeFirstLetter, formatDate } from 'src/utils';
import { FLAG_EN, FLAG_JA, FLAG_KO, FLAG_VI } from 'src/assets/images';
import styled from 'styled-components';

const StyledWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const initialValues: Study.StudyType = {
  id: '',
  createdAt: new Date(),
  updatedAt: new Date(),
  titleVi: '',
  titleEn: '',
  titleJa: '',
  titleKo: '',
  contentVi: '',
  contentEn: '',
  contentJa: '',
  contentKo: '',
  status: '',
  thumbnail: '',
  category: {
    id: '',
    createdAt: new Date(),
    updatedAt: new Date(),
    nameVi: '',
    nameEn: '',
    nameJa: '',
    nameKo: '',
  },
};

const CaseStudyScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const { studies, isLoading } = useAppSelector((state) => state.caseStudy);
  const { categories } = useAppSelector((state) => state.category);
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
  const [isModalFormOpen, setIsModalFormOpen] = useState(false);
  const [langue, setLangue] = useState(LANGUAGE.EN);
  const [caseStudy, setCaseStudy] = useState<Study.StudyType>(initialValues);
  const [filter, setFilter] = useState({
    keyword: '',
    status: '',
    categoryId: '',
  });
  const [currentPage, setCurrentPage] = useState(1);

  const pageSize = 10;

  const onFilterLangue = (value: string) => {
    setLangue(value);
  };

  const langues = [
    { label: <StyledWrapper> <Image src={FLAG_EN} /> EN </StyledWrapper>, value: LANGUAGE.EN },
    { label: <StyledWrapper> <Image src={FLAG_VI} /> VI </StyledWrapper>, value: LANGUAGE.VI },
    { label: <StyledWrapper> <Image src={FLAG_JA} /> JA </StyledWrapper>, value: LANGUAGE.JA },
    { label: <StyledWrapper> <Image src={FLAG_KO} /> KO </StyledWrapper>, value: LANGUAGE.KO },
  ]

  const columns: TableProps<any>['columns'] = [
    {
      title: '#',
      dataIndex: '#',
      key: '#',
      width: 80,
      onCell: () => ({ style: { minWidth: 80 } }),
      render: (id: string, _, index: number) => {
        const serialNumber = (currentPage - 1) * pageSize + (index + 1);
        return (
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '60px',
              whiteSpace: 'nowrap',
            }}
          >
            {serialNumber}
          </span>
        );
      },
    },
    {
      title: (
        <div className='langue-select'>
          <div>Title</div>
          <div>
            <Select
              style={{ width: 120 }}
              placeholder="langue"
              maxTagCount="responsive"
              options={langues}
              value={langue}
              onChange={(value) => onFilterLangue(value)}
            />
          </div>
        </div>
      ),
      dataIndex: `title${capitalizeFirstLetter(langue)}`,
      key: `title${capitalizeFirstLetter(langue)}`,
      onCell: () => ({ style: { minWidth: 200, maxWidth: 300 } }),
      render: (titleEn: string) => {
        return (
          <div
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
              whiteSpace: 'nowrap',
            }}
          >
            {titleEn}
          </div>
        );
      },
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 180,
      onCell: () => ({ style: { minWidth: 180 } }),
      render: (_, category: Study.StudyType) => {
        return <span>{category.category.nameEn}</span>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      onCell: () => ({ style: { minWidth: 120 } }),
      render: (status: string) => {
        return <span>{CASE_STUDY_STATUS[status]}</span>;
      },
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      onCell: () => ({ style: { minWidth: 180 } }),
      render: (createdAt: number) => {
        return <span>{formatDate(createdAt)}</span>;
      },
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      onCell: () => ({ style: { minWidth: 180 } }),
      render: (updatedAt: number) => {
        return <span>{formatDate(updatedAt)}</span>;
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 130,
      onCell: () => ({ style: { minWidth: 130 } }),
      render: (_, record) => {
        return (
          <div className="action-box">
            <Button
              onClick={(e) => {
                e.stopPropagation();
                setCaseStudy(record);
                setIsModalDeleteOpen(true);
              }}
            >
              <DeleteOutlined />
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                onEditUser(record);
              }}
            >
              <EditOutlined />
            </Button>
          </div>
        );
      },
    },
  ];

  console.log('columns: ', columns)

  useEffect(() => {
    getCategory();
    // fetchCaseStudy();
  }, []);

  useEffect(() => {
    fetchCaseStudy();
  }, [filter.status, filter.categoryId]);

  const fetchCaseStudy = (page = 1) => {
    const params: Study.GetStudiesParams = cleanFilter({
      ...filter,
      page,
      status: filter.status,
    });
    void dispatch(getCaseStudiesAction({ data: { ...params, limit: 10 } }));
    setCurrentPage(page);
  };

  const onFilterStatus = (value: string) => {
    setFilter({ ...filter, status: value });
  };

  const onFilterCategory = (value: string) => {
    setFilter({ ...filter, categoryId: value });
  };

  const getCategory = () => {
    void dispatch(getCategoryAction({ data: { limit: 20 } }));
  };

  const cleanFilter = <T extends Record<string, any>>(obj: T): T => {
    const cleanedObj: Partial<T> = {};

    Object.keys(obj).forEach((key) => {
      if (obj[key] !== null && obj[key] !== '') {
        cleanedObj[key as keyof T] = obj[key];
      }
    });

    return cleanedObj as T;
  };

  const onCreate = () => {
    // setCaseStudy(initUser);
    setIsModalFormOpen(true);
  };

  const onEditUser = (user: Study.StudyType) => {
    setUpUser(user);
    setIsModalFormOpen(true);
  };

  const handleDeleteOk = async () => {
    const result: any = await dispatch(
      deleteCaseStudiesAction({ data: caseStudy?.id })
    );
    setIsModalDeleteOpen(false);
    if (result?.error) {
      message.error('Delete failed');
    } else {
      message.success('Delete success');
      fetchCaseStudy();
    }
  };

  const setUpUser = (value: Study.StudyType) => {
    setCaseStudy(value);
  };

  // const onOpenModalDelete = () => {
  //   setIsModalDeleteOpen(true);
  // };

  const handleOkFormUser = () => {
    fetchCaseStudy();
    handleCancelModal();
  };

  const onChangePage = (value: number) => {
    fetchCaseStudy(value);
  };

  const handleCancelModal = () => {
    setIsModalFormOpen(false);
    setCaseStudy(initialValues);
  };

  const categoriesOptions = [
    { nameEn: 'All Categories', id: '' },
    ...categories?.records,
  ]?.map((item) => {
    return {
      label: item.nameEn,
      value: item.id,
    };
  });

  return (
    <CaseStudyStyle className="container">
      <SpinStyle
        spinning={isLoading}
        wrapperClassName="root-spin"
        className="root-spin-component"
      >
        <div className="header-content">
          <h2 className="title">Case studies</h2>
          <Button className="create-user" onClick={() => onCreate()}>
            Create Case studies
          </Button>
        </div>
        <div className="content">
          <div className="filter-box">
            <div className="total-box">
              Total: {studies?.total}
            </div>
            <div className="search-box">
              <Input
                placeholder="Search title..."
                prefix={<SearchOutlined />}
                onChange={(e) =>
                  setFilter({ ...filter, keyword: e.target.value })
                }
                onPressEnter={() => fetchCaseStudy()}
              />
              <Button onClick={() => fetchCaseStudy()}>Search</Button>
            </div>
            <Select
              style={{ width: 180 }}
              placeholder="Categories"
              maxTagCount="responsive"
              options={categoriesOptions}
              onChange={(value) => onFilterCategory(value)}
            />
            <Select
              style={{ width: 150 }}
              placeholder="Status"
              maxTagCount="responsive"
              options={CASE_STUDY_STATUS_OPTIONS}
              onChange={(value) => onFilterStatus(value)}
            />
          </div>
          <div className="data-table">
            {/* <Table
              dataSource={studies.records || []}
              columns={columns}
            /> */}
            <Pagination
              total={studies.total}
              onChange={onChangePage}
              current={currentPage}
              pageSize={studies.limit}
            />
          </div>
        </div>
        <DeleteModal
          isModalDeleteOpen={isModalDeleteOpen}
          handleCancel={() => setIsModalDeleteOpen(false)}
          handleOk={() => handleDeleteOk()}
        />
        {isModalFormOpen && (
          <FormModal
            isModalFormOpen={isModalFormOpen}
            studies={caseStudy}
            handleCancel={handleCancelModal}
            handleOk={handleOkFormUser}
          />
        )}
      </SpinStyle>
    </CaseStudyStyle>
  );
};

export default CaseStudyScreen;
